import { Routes } from '@angular/router';

/**
 * Recursively extracts all resolved URL paths from an Angular Routes array.
 * - :param → {param}
 * - redirectTo → uses the redirect target
 * - children → prefixed with parent path
 * - Skips wildcards (**) and empty paths ('')
 */
export function extractRouteLinks(routes: Routes, parentPath = ''): string[] {
  const links: string[] = [];

  for (const route of routes) {
    // Resolve the effective path
    let rawPath: string;

    if (route.redirectTo) {
      // redirectTo may already be absolute (starts with '/') or relative
      rawPath = route.redirectTo.toString().startsWith('/')
        ? route.redirectTo.toString().slice(1)
        : route.redirectTo.toString();
    } else {
      rawPath = route.path ?? '';
    }

    // Skip wildcards and empty paths
    if (rawPath === '**' || rawPath === '') {
      continue;
    }

    // Convert :param segments to {param}
    const normalised = rawPath
      .split('/')
      .map(seg => seg.startsWith(':') ? `{${seg.slice(1)}}` : seg)
      .join('/');

    const fullPath = parentPath
      ? `${parentPath}/${normalised}`
      : `/${normalised}`;

    // Avoid duplicates
    if (!links.includes(fullPath)) {
      links.push(fullPath);
    }

    // Recurse into children (only when there is no redirect)
    if (!route.redirectTo && route.children?.length) {
      const childLinks = extractRouteLinks(route.children, fullPath);
      for (const cl of childLinks) {
        if (!links.includes(cl)) {
          links.push(cl);
        }
      }
    }
  }

  return links;
}
